import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getSymbols, updateSymbol } from '../../services/SymbolsService';
import { getCockpit } from '../../services/BeholderService';
import { formatNumber } from '../../utils/NumberUtils';
import SignalHistoryModal from './SignalHistoryModal';

function PriceChange({ value }) {
    const num = parseFloat(value) || 0;
    const color = num > 0 ? 'text-success' : num < 0 ? 'text-danger' : 'text-muted';
    const sign = num > 0 ? '+' : '';
    return <span className={color}>{sign}{formatNumber(num, 2)}%</span>;
}

// ─── Helpers de renderização das colunas do Cockpit ──────────────────────

const REGIME_DISPLAY = {
    TRENDING_UP:     { emoji: '📈', label: 'Alta',    color: 'text-success' },
    TRENDING_DOWN:   { emoji: '📉', label: 'Baixa',   color: 'text-danger'  },
    RANGING:         { emoji: '↔️', label: 'Lateral', color: 'text-info'    },
    HIGH_VOLATILITY: { emoji: '⚡', label: 'Volátil', color: 'text-warning' }
};

function RegimeCell({ regime }) {
    if (!regime || !regime.regime) return <span className='text-muted'>—</span>;
    const d = REGIME_DISPLAY[regime.regime];
    if (!d) return <span className='text-muted'>{regime.regime}</span>;
    const conf = regime.confidence != null ? `${Math.round(regime.confidence * 100)}%` : '';
    const tooltip = `${d.label}${conf ? ` · confiança ${conf}` : ''}${regime.adx ? ` (ADX ${regime.adx})` : ''}`;
    return (
        <span className={d.color} title={tooltip} style={{ fontSize: 13 }}>
            <span className='me-1'>{d.emoji}</span>{d.label}
            {conf && <small className='text-muted ms-1'>{conf}</small>}
        </span>
    );
}

const SIGNAL_DISPLAY = {
    BUY:  { emoji: '🟢', label: 'BUY',  cls: 'badge bg-success' },
    SELL: { emoji: '🔴', label: 'SELL', cls: 'badge bg-danger'  },
    HOLD: { emoji: '⏸',  label: 'HOLD', cls: 'badge bg-secondary' }
};

function SignalCell({ signal }) {
    if (!signal || !signal.signal) return <span className='text-muted'>—</span>;
    const d = SIGNAL_DISPLAY[signal.signal];
    if (!d) return <span className='text-muted'>{signal.signal}</span>;
    const conf = signal.confidence != null ? ` ${Math.round(signal.confidence * 100)}%` : '';
    const tooltip = signal.reasoning || `${d.label}${conf}`;
    return (
        <span className={d.cls} title={tooltip} style={{ fontSize: 12 }}>
            {d.emoji} {d.label}{conf}
        </span>
    );
}

// Renderiza "Aguardando 1/3: {pretty}" quando há automação ativa quase
// pronta. Mostra só a "mais próxima" (maior passing/total) pra evitar
// poluir; tooltip lista as outras quando há mais.
function DiagnosisHint({ diagnoses }) {
    if (!diagnoses || diagnoses.length === 0) return null;
    const pending = diagnoses.filter(d => !d.ready && d.total > 0 && d.passing >= 1);
    if (pending.length === 0) return null;
    pending.sort((a, b) => (b.passing / b.total) - (a.passing / a.total));
    const top = pending[0];
    const firstMissing = top.missing && top.missing[0];
    if (!firstMissing) return null;
    const tooltipLines = [
        `Automação: ${top.name}`,
        `Condições: ${top.passing}/${top.total}`,
        ...top.missing.map(m => `· ${m.pretty || m.expr}`)
    ];
    if (pending.length > 1) tooltipLines.push(`(+ ${pending.length - 1} outras automações aguardando)`);
    return (
        <div className='mt-1' style={{ fontSize: 11 }} title={tooltipLines.join('\n')}>
            <span className='text-muted'>⏳ {top.passing}/{top.total} · falta:</span>{' '}
            <span className='text-warning'>{firstMissing.pretty || firstMissing.expr}</span>
        </div>
    );
}

function StatusCell({ cockpit, currentPrice, symbol, onAiOrder }) {
    if (!cockpit) return <span className='text-muted'>—</span>;

    const { signal, lastOrder, diagnoses } = cockpit;
    const hasOpenBuy = lastOrder && lastOrder.side === 'BUY' && lastOrder.status === 'FILLED';

    const sellButton = (
        <button
            type='button'
            className='btn btn-sm btn-outline-danger ms-2 py-0 px-2'
            style={{ fontSize: 11 }}
            title='Abrir New Order pré-preenchido com a sugestão de venda da IA'
            onClick={() => onAiOrder && onAiOrder(symbol, signal)}>
            🔴 Vender agora →
        </button>
    );

    const buyButton = (
        <button
            type='button'
            className='btn btn-sm btn-outline-success ms-2 py-0 px-2'
            style={{ fontSize: 11 }}
            title='Abrir New Order pré-preenchido com a sugestão de compra da IA'
            onClick={() => onAiOrder && onAiOrder(symbol, signal)}>
            🟢 Comprar agora →
        </button>
    );

    // Posição aberta — calcula P&L se tivermos preço atual
    if (hasOpenBuy) {
        const avg = parseFloat(lastOrder.avgPrice || lastOrder.limitPrice || 0);
        const isSellSignal = signal && signal.signal === 'SELL';
        if (currentPrice && avg > 0) {
            const pnlPct = ((currentPrice - avg) / avg) * 100;
            const cls = pnlPct >= 0 ? 'text-success' : 'text-danger';
            const sign = pnlPct >= 0 ? '+' : '';
            return (
                <span title={`Posição comprada @ ${avg}`}>
                    <span className={cls} style={{ fontSize: 13 }}>
                        Em posição: {sign}{pnlPct.toFixed(2)}%
                    </span>
                    {isSellSignal && (
                        <span className='d-block mt-1'>
                            <small className='text-warning' style={{ fontSize: 11 }}>⚠ IA: vender</small>
                            {sellButton}
                        </span>
                    )}
                    <DiagnosisHint diagnoses={diagnoses} />
                </span>
            );
        }
        return (
            <span title='Posição aberta'>
                <span className='text-info' style={{ fontSize: 13 }}>Em posição</span>
                {isSellSignal && sellButton}
                <DiagnosisHint diagnoses={diagnoses} />
            </span>
        );
    }

    if (signal && signal.signal === 'BUY') {
        const conf = signal.confidence != null ? Math.round(signal.confidence * 100) : null;
        return (
            <span title={signal.reasoning || ''}>
                <span className='text-success' style={{ fontSize: 13 }}>
                    Pronto pra entrar{conf ? ` (${conf}%)` : ''}
                </span>
                {buyButton}
                <DiagnosisHint diagnoses={diagnoses} />
            </span>
        );
    }

    if (signal && signal.signal === 'HOLD') {
        return (
            <>
                <span className='text-muted' style={{ fontSize: 13 }}>Aguardando setup</span>
                <DiagnosisHint diagnoses={diagnoses} />
            </>
        );
    }

    // Sem sinal claro, mas pode haver automação parcialmente avançada
    if (diagnoses && diagnoses.some(d => !d.ready && d.passing >= 1)) {
        return (
            <>
                <span className='text-muted' style={{ fontSize: 13 }}>—</span>
                <DiagnosisHint diagnoses={diagnoses} />
            </>
        );
    }

    return <span className='text-muted'>—</span>;
}

function Watchlist({ tickerData, onAiOrder }) {
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cockpit, setCockpit] = useState({});
    const [historySymbol, setHistorySymbol] = useState(null);

    useEffect(() => {
        getSymbols(true)
            .then(result => setFavorites((result.rows || []).map(s => s.symbol)))
            .catch(() => setFavorites([]))
            .finally(() => setLoading(false));
    }, []);

    const loadCockpit = useCallback(() => {
        if (!favorites.length) return;
        getCockpit(favorites)
            .then(data => setCockpit(data || {}))
            .catch(() => {});
    }, [favorites]);

    // Carga inicial + refresh a cada 30s (dados do Redis mudam ao ritmo
    // dos monitores; refresh frequente captura mudanças sem ser intrusivo)
    useEffect(() => {
        loadCockpit();
        if (!favorites.length) return undefined;
        const id = setInterval(loadCockpit, 30000);
        return () => clearInterval(id);
    }, [loadCockpit, favorites.length]);

    function handleRemoveFavorite(symbol) {
        updateSymbol({ symbol, isFavorite: false })
            .then(() => setFavorites(prev => prev.filter(s => s !== symbol)))
            .catch(() => {});
    }

    if (loading) return null;
    if (!favorites.length) return (
        <div className='card border-0 shadow mb-4'>
            <div className='card-header d-flex justify-content-between align-items-center'>
                <h2 className='fs-5 fw-bold mb-0'>Favoritos</h2>
                <Link to='/symbols' className='btn btn-sm btn-outline-primary'>Adicionar</Link>
            </div>
            <div className='card-body text-muted py-3' style={{ fontSize: 14 }}>
                Nenhum símbolo favoritado ainda.{' '}
                <Link to='/symbols'>Adicione favoritos</Link> para acompanhá-los aqui.
            </div>
        </div>
    );

    return (
        <div className='card border-0 shadow mb-4'>
            <div className='card-header d-flex justify-content-between align-items-center'>
                <h2 className='fs-5 fw-bold mb-0'>Favoritos · Cockpit</h2>
                <div className='d-flex align-items-center gap-2'>
                    <button
                        className='btn btn-sm btn-link p-0 text-secondary'
                        onClick={loadCockpit}
                        title='Recarregar dados do cockpit'>↻</button>
                    <Link to='/symbols' className='btn btn-sm btn-outline-primary'>Gerenciar</Link>
                </div>
            </div>
            <div className='table-responsive'>
                <table className='table table-sm table-hover align-items-center mb-0'>
                    <thead className='thead-light'>
                        <tr>
                            <th className='border-bottom'>Símbolo</th>
                            <th className='border-bottom text-end'>Preço</th>
                            <th className='border-bottom text-end'>Var. 24h</th>
                            <th className='border-bottom'>Regime</th>
                            <th className='border-bottom'>Sinal IA</th>
                            <th className='border-bottom'>Status</th>
                            <th className='border-bottom'></th>
                        </tr>
                    </thead>
                    <tbody>
                        {favorites.map(symbol => {
                            const d = tickerData && tickerData[symbol];
                            const c = cockpit[symbol];
                            const currentPrice = d ? parseFloat(d.close) : null;
                            return (
                                <tr key={symbol}>
                                    <td className='fw-semibold'>
                                        <a href={`https://br.tradingview.com/symbols/${symbol}`}
                                            target='_blank' rel='noreferrer'
                                            className='badge bg-info text-white'
                                            style={{ fontSize: '0.8rem' }}>
                                            {symbol}
                                        </a>
                                    </td>
                                    <td className='text-end'>{d ? formatNumber(d.close) : '—'}</td>
                                    <td className='text-end'>
                                        {d ? <PriceChange value={d.percentChange} /> : '—'}
                                    </td>
                                    <td><RegimeCell regime={c && c.regime} /></td>
                                    <td><SignalCell signal={c && c.signal} /></td>
                                    <td><StatusCell cockpit={c} currentPrice={currentPrice} symbol={symbol} onAiOrder={onAiOrder} /></td>
                                    <td className='text-end'>
                                        <button
                                            className='btn btn-sm btn-link p-0 me-2'
                                            title='Ver últimos sinais da IA para este símbolo'
                                            onClick={() => setHistorySymbol(symbol)}
                                            style={{ fontSize: 14 }}
                                        >
                                            📜
                                        </button>
                                        <button
                                            className='btn btn-sm btn-link text-danger p-0'
                                            title='Remover dos favoritos'
                                            onClick={() => handleRemoveFavorite(symbol)}
                                        >
                                            ★
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <SignalHistoryModal
                symbol={historySymbol}
                onClose={() => setHistorySymbol(null)}
            />
        </div>
    );
}

export default Watchlist;
